
import { Options, Vue } from "vue-class-component";
import Card from "@/components/Card.vue";
import PeekBox from "@/components/PeekBox.vue";
import axios from "axios";

const smallWindow = window.matchMedia("(max-width: 640px)");

let scrollToId = "";

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

@Options({
  data() {
    return {
      title: "透明盒子计划·笔记",
      notePathList: [],
      noteContentList: [],
      noteCache: {},
      metadataCache: {},
      peekPath: "",
      selectedId: -1,
      showPeekBox: false,
      isInPeekBox: false,
      timer: "",
      eventClientX: 0,
      eventClientY: 0
    };
  },
  components: {
    Card,
    PeekBox
  },
  watch: {
    $route(to, from) {
      if (to.name !== "Notes") return;
      if (smallWindow.matches) {
        this.loadRoute(to);
        setTimeout(() => {
          document.getElementById("app")?.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }, 100);
        return;
      }
      let fromStack = from.query.stack || [];
      let toStack = to.query.stack || [];

      if (typeof fromStack === "string") fromStack = [fromStack];
      if (typeof toStack === "string") toStack = [toStack];

      fromStack = [from.params.noteName.split(".")[0], ...fromStack];
      toStack = [to.params.noteName.split(".")[0], ...toStack];

      let newCards = [];
      let removedCards = [];
      if (fromStack && toStack) {
        newCards = toStack.filter(item => fromStack.indexOf(item) < 0);
        removedCards = fromStack.filter(item => toStack.indexOf(item) < 0);
      } else {
        if (toStack) newCards = toStack;
        if (fromStack) removedCards = fromStack;
      }

      if (newCards.length > 0) {
        newCards
          .map(item => [this.stackCardToPath(item), toStack.indexOf(item)])
          .forEach(item => this.fetchNote(item[0], item[1]));
      }

      if (removedCards.length > 0) {
        this.removeNotesData(removedCards);
      }

      this.updateTitle();
    },
    selectedId: function(val) {
      sleep(100).then(() => {
        const card = document.getElementById(val);
        if (card) {
          card.tabIndex = 0;
          if (
            !smallWindow.matches &&
            (card.matches(".covered") ||
              card.matches(".tiny-view") ||
              val === this.notePathList.length - 1)
          ) {
            this.scrollToElement(val);
          }
          sleep(100).then(() => card.focus());
        }
      });
    }
  },
  beforeMount() {
    this.loadRoute(this.$route);
  },
  mounted() {
    document.addEventListener("keyup", this.handleKeyPressed);
    this.updateTitle();
  },
  updated() {
    if (scrollToId === "" || smallWindow.matches) return;
    this.scrollToElement(scrollToId);
    scrollToId = "";
  },
  beforeUnmount() {
    document.removeEventListener("keyup", this.handleKeyPressed);
  },
  methods: {
    loadRoute(route) {
      this.fetchNote(route.params.noteName);
      let stack = route.query.stack;
      if (stack) {
        if (typeof route.query.stack === "string") stack = [stack];
        stack
          .map(s => this.stackCardToPath(s))
          .forEach((p, i) => this.fetchNote(p, i + 1));
      }
    },
    addNotesDataForCard(path: string, content: string, index = -1) {
      const decodedPath = decodeURI(path);
      if (index < 0) {
        this.notePathList.push(decodedPath);
        this.noteContentList.push(content);
      } else {
        this.notePathList.splice(index, 0, decodedPath);
        this.noteContentList.splice(index, 0, content);
      }
    },
    stackCardToPath(card: string) {
      return `${decodeURI(card)}.html`;
    },
    removeNotesData(stackToBeRemoved: string[]) {
      stackToBeRemoved.forEach(card => {
        const index = this.notePathList.indexOf(this.stackCardToPath(card));
        this.removeNotesDataByIndex(index);
      });
    },
    removeNotesDataByIndex(index = -1) {
      if (index >= 0) {
        this.notePathList.splice(index, 1);
        this.noteContentList.splice(index, 1);
      }
    },
    resetNotesData() {
      this.notePathList = [];
      this.noteContentList = [];
    },
    pageMetadataPath(pagePath) {
      if (pagePath.endsWith(".html")) {
        return pagePath.replace(".html", ".json");
      }
      return false;
    },
    fetchNote(path: string, index = 0, justPeeking = false) {
      const data = this.noteCache[path];
      if (data) {
        this.fillComponent(path, index, data, justPeeking);
        return;
      }
      axios
        .get(`/notes/${path}`)
        .then(res => {
          this.noteCache[path] = res.data;
          this.fillComponent(path, index, res.data, justPeeking);
        })
        .catch(err => {
          console.log(err);
        });
      const metadataPath = this.pageMetadataPath(path);
      if (metadataPath) {
        axios
          .get(`/notes/${metadataPath}`)
          .then(res => {
            this.metadataCache[metadataPath] = res.data;
          })
          .catch(err => {
            console.log(err);
          });
      }
    },
    metadataForIndex(index) {
      const path = this.notePathList[index];
      const metadataPath = this.pageMetadataPath(path);
      if (metadataPath) {
        return this.metadataCache[metadataPath];
      }
      return undefined;
    },
    fillComponent(path, index, data, justPeeking) {
      if (justPeeking) {
        this.peekPath = path;
        this.showPeekBox = true;
      } else {
        if (smallWindow.matches) {
          this.notePathList = [path];
          this.noteContentList = [data];
        } else {
          this.addNotesDataForCard(path, data, index);
        }
      }
    },
    scrollToElement(id: string) {
      sleep(100).then(() => {
        const el = document.getElementById(id);

        if (el) {
          // Use el.scrollIntoView() to instantly scroll to the element
          // el.scrollIntoView({ behavior: "smooth", inline: "end" });
          el.parentElement?.parentElement?.scrollTo({
            top: 0,
            left: (el.offsetWidth - 42) * Number(id),
            behavior: "smooth"
          });
        }
      });
    },
    scrollByCard(count = 1) {
      if (this.selectedId === -1) {
        this.selectedId = this.getScrollToElementId();
      }
      this.selectedId += count;

      if (this.selectedId === -1)
        this.selectedId = this.notePathList.length - 1;
      if (this.selectedId > this.notePathList.length - 1)
        this.selectedId = this.selectedId % this.notePathList.length;
      this.scrollToElement(this.selectedId);
    },
    getScrollToElementId() {
      const {
        scrollLeft,
        scrollWidth,
        offsetWidth
      } = document.getElementsByClassName("home")[0] as HTMLElement;
      const cardWidth = (document.getElementsByClassName(
        "card"
      )[0] as HTMLElement).offsetWidth;
      // if (scrollLeft === scrollWidth - offsetWidth) {
      //   return Math.ceil(scrollLeft / cardWidth);
      // }
      return Math.ceil(scrollLeft / cardWidth);
    },
    handleKeyPressed(event) {
      switch (event.key) {
        case "ArrowRight":
        case "l":
          this.scrollByCard();
          break;
        case "h":
        case "ArrowLeft":
          this.scrollByCard(-1);
          break;
      }
    },
    handleClose(event) {
      const index = Number(event.target.closest(".card").id);
      if (index >= 0) {
        const tempPathList = [...this.notePathList];
        tempPathList.splice(index, 1);
        this.pushRouterPath(tempPathList);
      }
    },
    pushRouterPath(pathList: string[]) {
      if (pathList.length <= 0) {
        pathList = ["/"];
      }
      this.$router.push({
        path: pathList.shift(),
        query: {
          stack: pathList.map(p => p.split(".")[0])
        }
      });
    },
    updateTitle() {
      setTimeout(() => {
        const docTitle = this.$el.getElementsByClassName("note-title")[0];
        if (docTitle) {
          this.title = `透明盒子计划·笔记·${docTitle.innerText}`;
        } else {
          this.title = "透明盒子计划·笔记";
        }
      }, 600);
    },
    handleOverLink(event) {
      if (smallWindow.matches) return;
      this.timer = setTimeout(() => {
        const to = this.filterClickEvent(event);
        this.eventClientX = event.clientX;
        this.eventClientY = event.clientY;

        if (to) {
          if (to === this.peekPath && this.showPeekBox) return;
          const content = this.noteCache[to];
          if (content) {
            this.peekPath = to;
            this.showPeekBox = true;
          }
          this.fetchNote(to, 0, true);
        }
      }, 1000);
    },
    handleLeaveLink(event) {
      clearTimeout(this.timer);
      sleep(200).then(() => {
        if (!this.isInPeekBox) this.showPeekBox = false;
      });
    },
    handleClick(event) {
      const to = this.filterClickEvent(event);
      if (to) {
        event.preventDefault();
        if (smallWindow.matches) {
          this.$router.push({
            path: to
          });
        } else {
          const decodedTo = decodeURI(to);
          if (this.notePathList.includes(decodedTo)) {
            this.scrollToElement(this.notePathList.indexOf(decodedTo));
            return;
          }
          const newCardId = Number(event.target.closest(".card").id) + 1;
          scrollToId = newCardId.toString();
          this.selectedId = newCardId;
          const tempPathList = [...this.notePathList];
          tempPathList.splice(newCardId, 0, to);
          this.pushRouterPath(tempPathList);
        }
      }
    },
    filterClickEvent(event) {
      // ensure we use the link, in case the click has been received by a subelement
      let { target } = event;
      const cardId = Number(target.closest(".card").id);
      if (target.matches(".note-title")) return this.notePathList[cardId];
      if (!target.matches("a")) this.selectedId = cardId;
      while (target && target.tagName !== "A") target = target.parentNode;
      // handle only links that occur inside the component and do not reference external resources
      if (
        target &&
        target.matches(".card a:not([href*='://'])") &&
        target.href
      ) {
        // some sanity checks taken from vue-router:
        // https://github.com/vuejs/vue-router/blob/dev/src/components/link.js#L106
        const {
          altKey,
          ctrlKey,
          metaKey,
          shiftKey,
          button,
          defaultPrevented
        } = event;
        // don't handle with control keys
        if (metaKey || altKey || ctrlKey || shiftKey) return false;
        // don't handle when preventDefault called
        // if (defaultPrevented) return
        // don't handle right clicks
        if (button !== undefined && button !== 0) return false;
        // don't handle if `target="_blank"`
        if (target && target.getAttribute) {
          const linkTarget = target.getAttribute("target");
          if (/\b_blank\b/i.test(linkTarget)) return false;
        }
        // don't handle same page links/anchors
        const url = new URL(target.href);
        const to = url.pathname.split("/")[1];
        if (event.preventDefault) {
          return to;
        } else {
          return false;
        }
      }
    }
  }
})
export default class Home extends Vue {}
